import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import MarketOverview from 'sections/lp-analysis/Market/MarketOverviewResolver';
import MarketTrends from 'sections/lp-analysis/MarketTrends';
import { openModal } from 'store/reducers/actions';

/**
 * Dedicated report viewer for Market Analysis Tool
 * Only renders MarketOverview and MarketTrends components
 * Does not include other LP analysis fields (USP, SWOT, segmentation, etc.)
 */
const MarketAnalysisReportViewer = ({ reportData }) => {
  const dispatch = useDispatch();
  const containerRef = React.useRef(null);
  const plan = useSelector((state) => state.proAccountReducer.plan);
  const trialWasActivated = useSelector((state) => state.proAccountReducer.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
  const isProUser = plan === 'Trial' || plan === 'Pro' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';

  // Extract market analysis data from reportData
  // Support multiple possible data structures
  const marketData = React.useMemo(() => {
    if (!reportData) return null;

    // handleOpenReport passes data as reportData.analysisData = toolCall.tool_result
    // So check analysisData first, then fall back to other locations
    const toolResult =
      reportData.analysisData ||
      reportData.tool_result ||
      reportData.data?.tool_result ||
      reportData.data ||
      reportData.result?.tool_result ||
      reportData.result ||
      reportData.content?.tool_result ||
      reportData.content ||
      reportData.metadata?.tool_result ||
      reportData.metadata ||
      reportData;

    // Extract market_overview and market_trends
    const marketOverview = toolResult?.market_overview || toolResult?.marketOverview;
    const marketTrends = toolResult?.market_trends || toolResult?.marketTrends;

    if (marketOverview && marketTrends) {
      return {
        market_overview: marketOverview,
        market_trends: {
          trends: marketTrends.trends || [],
          benchmarks: marketTrends.benchmarks || [],
          industry: marketTrends.industry || ''
        }
      };
    }

    return null;
  }, [reportData]);

  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Get access to detailed market analysis',
        subtitle: "Upgrade to Pro to view all market insights and trends. Don't miss out on accessing more data!",
        trial: trialAllowed
      })
    );
  };

  // Citation highlighting and scrolling functionality
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const clearExternalHighlights = () => {
      if (!container) return;
      const nodes = container.querySelectorAll('.external-citation-highlight');
      nodes.forEach((node) => {
        const parent = node.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(node.textContent), node);
        parent.normalize();
      });
    };

    const applyExternalHighlight = (text) => {
      if (!container || !text) return false;
      const base = String(text).trim();
      if (!base) return false;
      // Build tolerant variants: raw, raw+..., raw+…, swap .../… inside, strip fancy quotes
      const ensureEllipsis = (s) => (/[.…]$/.test(s) || /\.\.\.$/.test(s) ? s : `${s}…`);
      const swapDots = (s) => s.replace(/\.\.\./g, '…');
      const swapEllipsis = (s) => s.replace(/…/g, '...');
      const stripQuotes = (s) => s.replace(/[""]/g, '"').replace(/['']/g, "'");
      const variants = Array.from(
        new Set([
          base,
          ensureEllipsis(base),
          swapDots(base),
          swapDots(ensureEllipsis(base)),
          swapEllipsis(base),
          stripQuotes(base),
          stripQuotes(ensureEllipsis(base))
        ])
      );
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        const content = node.textContent || '';
        const contentLower = content.toLowerCase();
        let foundStart = -1;
        let foundLen = 0;
        for (const v of variants) {
          const vLower = String(v).toLowerCase();
          const i = contentLower.indexOf(vLower);
          if (i !== -1) {
            foundStart = i;
            foundLen = vLower.length;
            break;
          }
        }
        if (foundStart !== -1) {
          try {
            const range = document.createRange();
            range.setStart(node, foundStart);
            range.setEnd(node, foundStart + foundLen);
            const span = document.createElement('span');
            span.className = 'external-citation-highlight';
            span.style.backgroundColor = 'rgba(255, 215, 0, 0.35)';
            span.style.borderRadius = '2px';
            span.style.padding = '1px 2px';
            try {
              range.surroundContents(span);
            } catch (e) {
              span.appendChild(range.extractContents());
              range.insertNode(span);
            }
            // Scroll into view
            setTimeout(() => {
              try {
                span.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } catch (e) {
                /* ignore */
              }
            }, 0);
            return true;
          } catch (e) {
            // continue scanning
          }
        }
      }
      return false;
    };

    const onHighlight = (evt) => {
      const text = evt && evt.detail ? evt.detail.text : '';
      if (!text) return;
      clearExternalHighlights();
      // try exact first, then case-insensitive (already lowercased above)
      if (!applyExternalHighlight(text)) {
        // as a last resort, strip quotes that may differ
        const stripped = String(text)
          .replace(/["'""]/g, '')
          .trim();
        if (stripped) applyExternalHighlight(stripped);
      }
    };

    const onClear = () => clearExternalHighlights();

    window.addEventListener('market-analysis:highlight', onHighlight);
    window.addEventListener('market-analysis:clear-highlight', onClear);
    return () => {
      window.removeEventListener('market-analysis:highlight', onHighlight);
      window.removeEventListener('market-analysis:clear-highlight', onClear);
      clearExternalHighlights();
    };
  }, []);

  if (!marketData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Market analysis data not available.
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} ref={containerRef}>
      <MarketOverview
        data={marketData.market_overview}
        forceV2={true}
        allowAnyRegions={true}
        isProUser={isProUser}
        handleTrialClick={handleTrialClick}
        trialAllowed={trialAllowed}
      />
      <MarketTrends marketTrends={marketData.market_trends} />
    </Container>
  );
};

export default MarketAnalysisReportViewer;
